import * as React from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { isAfter } from "date-fns";

import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Avatar from "@material-ui/core/Avatar";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PhoneIcon from "@material-ui/icons/Phone";
import AlternateEmailIcon from "@material-ui/icons/AlternateEmail";
import DoneIcon from "@material-ui/icons/Done";
import ClearIcon from "@material-ui/icons/Clear";
import ReplyIcon from "@material-ui/icons/Reply";
import SnoozeIcon from "@material-ui/icons/Snooze";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import PeopleIcon from "@material-ui/icons/People";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import TableChartIcon from "@material-ui/icons/TableChartOutlined";
import SettingsBackupRestore from "@material-ui/icons/SettingsBackupRestore";
import InfoIcon from "@material-ui/icons/Info";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import ChatIcon from "@material-ui/icons/Chat";
import LayersIcon from "@material-ui/icons/Layers";
import RestaurantIcon from "@material-ui/icons/RestaurantMenu";
import { Inquiry as InquiryIFace } from "../../api/inquiries";
import { restoreArchivedInquiry, updateInquiry } from "../../actions/inquiry-actions";
import { format, formatDate } from "../../util/date-utility";
import { StoreState } from '../../reducers/root-reducer';
import { getReadablePaymentCurrency, nl2br } from '../../util/general-utility';
import { Dispatch } from 'redux';
import { openAddPaymentDialog, openEditInquiryDialog } from '../../actions/store/app-actions';
import { Payment } from '../../api/payments';

interface State {
	actionsMenuAnchor: HTMLElement;
	showConfirmation: boolean;
	confirmationLabel: string;
	confirmationFunc: (confirmed: boolean) => void;
}

export interface InquiryProps {
	data: InquiryIFace;
	payment: Payment;
	highlighted: boolean;
	onReply: (data: InquiryIFace) => void;
}

type Props = InquiryProps &
	ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps> &
	WithStyles<typeof styles>;

export class Inquiry extends React.Component<Props, State> {
	public state: State = {
		actionsMenuAnchor: null,
		showConfirmation: false,
		confirmationLabel: "",
		confirmationFunc: null
	};

	private handleSnoozeClick = () => {
		const { id, snoozed } = this.props.data;

		updateInquiry(id, {
			snoozed: !snoozed
		});
	};

	private handleRestoreClick = (confirmed = false) => {
		if (confirmed) {
			const { id } = this.props.data;
			restoreArchivedInquiry(id);
		} else {
			this.setState({
				showConfirmation: true,
				confirmationLabel: "Wiederherstellen?",
				confirmationFunc: this.handleRestoreClick
			});
		}
	};

	private handleReplyClick = () => {
		this.props.onReply(this.props.data);
	};
	
	private handleConvertToReservervationClick = async(confirmed = false) => {
		if (confirmed) {
			const { id, payment } = this.props.data;

			await updateInquiry(id, {
				moved: true
			});
		} else {
			this.setState({
				showConfirmation: true,
				confirmationLabel: "Zu den Reservierungen verschieben?",
				confirmationFunc: this.handleConvertToReservervationClick
			});
		}
	};

	private handleRequestPaymentClick = () => {
		const { id, email, primaryName } = this.props.data;

		this.props.openAddPaymentDialog(id, email, primaryName);
	};

	private handleRejectClick = (confirmed = false) => {
		if (confirmed) {
			const { id } = this.props.data;

			updateInquiry(id, {
				rejected: true
			});
		} else {
			this.setState({
				showConfirmation: true,
				confirmationLabel: "Absagen?",
				confirmationFunc: this.handleRejectClick
			});
		}
	};

	private handleDeleteClick = (confirmed = false) => {
		if (confirmed) {
			const { id } = this.props.data;

			updateInquiry(id, {
				deleted: true
			});
		} else {
			this.setState({
				showConfirmation: true,
				confirmationLabel: "Löschen?",
				confirmationFunc: this.handleDeleteClick
			});
		}
	};

	private handleEditClick = () => {
		const { data, openEditInquiryDialog } = this.props;

		openEditInquiryDialog(data as any);
	};

	private handleAcceptClick = async(confirmed = false) => {
		if (confirmed) {
			const { id } = this.props.data;

			await updateInquiry(id, {
				accepted: true
			});
		} else {
			this.setState({
				showConfirmation: true,
				confirmationLabel: "Zusagen?",
				confirmationFunc: this.handleAcceptClick
			});
		}
	};

	private handleConfirmationOKClick = () => {
		this.state.confirmationFunc(true);

		this.setState({
			showConfirmation: false,
			confirmationFunc: null
		});
	};

	private handleConfirmationCancelClick = () => {
		this.setState({
			showConfirmation: false,
			confirmationFunc: null
		});
	};

	private handleActionsMenuClose = () => {
		this.setState({ actionsMenuAnchor: null });
	};

	private handleActionsMenuButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		this.setState({ actionsMenuAnchor: event.currentTarget });
	};

	private renderPaymentInfo = () => {
		const { payment } = this.props.data;

		let info = getReadablePaymentCurrency(payment.currency, payment.amount);

		info += "<br />";

		switch (payment.state) {
			case "awaiting_payment":
				info += "Gültig bis zum " + format(payment.expirationDate, "DD.MM. HH:mm");
				break;

			case "expired":
				info += "Abgelaufen am " + format(payment.expirationDate, "DD.MM. HH:mm");
				break;

			case "completed":
				info += "Bezahlt am " + format(payment.completionTime, "DD.MM. HH:mm");
				break;
		}

		return info;
	};

	private renderCardHeaderAction = () => {
		const { classes } = this.props;
		const { actionsMenuAnchor } = this.state;

		return (
			<React.Fragment>
				<IconButton onClick={this.handleActionsMenuButtonClick}>
					<MoreVertIcon />
				</IconButton>
				<Menu
					anchorEl={actionsMenuAnchor}
					open={Boolean(actionsMenuAnchor)}
					onClick={this.handleActionsMenuClose}
				>
					<MenuItem
						className={classes.editButton}
						onClick={this.handleEditClick}
					>
						Bearbeiten
					</MenuItem>
					<MenuItem
						className={classes.deleteButton}
						onClick={() => this.handleDeleteClick()}
					>
						Löschen
					</MenuItem>
				</Menu>
			</React.Fragment>
		);
	};

	private renderCardActions = () => {
		const { showConfirmation, confirmationLabel } = this.state;
		const { classes } = this.props;
		const {
			rejected,
			payment
		} = this.props.data;

		return (
			<CardActions>
					<div className={classes.cardActions}>
						<div
							className={classNames(classes.confirmationActions, {
								[classes.confirmationActionsVisible]: showConfirmation
							})}
						>
							<Typography className={classes.confirmationLabel}>
								{confirmationLabel}
							</Typography>
							<IconButton
								className={classes.confirmationOKButton}
								onClick={this.handleConfirmationOKClick}
							>
								<DoneIcon />
							</IconButton>
							<IconButton
								className={classes.confirmationCancelButton}
								onClick={this.handleConfirmationCancelClick}
							>
								<ClearIcon />
							</IconButton>
						</div>
						{rejected ? (
							<div className={classes.buttonContainer}>
								<IconButton
									className={classes.snoozeButton}
									onClick={() => this.handleRestoreClick()}
								>
									<SettingsBackupRestore />
								</IconButton>
							</div>
						) : (
							<div className={classes.buttonContainer}>
								<IconButton
									className={classes.acceptButton}
									onClick={() => this.handleAcceptClick()}
								>
									<DoneIcon />
								</IconButton>
								<IconButton
									className={classes.rejectButton}
									onClick={() => this.handleRejectClick()}
								>
									<ClearIcon />
								</IconButton>
								<div className={classes.flexExpand} />
								<IconButton
									className={classes.replyButton}
									onClick={this.handleReplyClick}
								>
									<ReplyIcon />
								</IconButton>

								{(payment && (payment.state === "awaiting_payment" || payment.state === "completed")) ? (
									<IconButton
										className={classes.convertToReservervationButton}
										onClick={() => this.handleConvertToReservervationClick()}
									>
										<TableChartIcon />
									</IconButton>
								) : (
									<IconButton
										className={classes.requestPaymentButton}
										onClick={this.handleRequestPaymentClick}
									>
									<AttachMoneyIcon />
									</IconButton>
								)}
								<IconButton
									className={classes.snoozeButton}
									onClick={this.handleSnoozeClick}
								>
									<SnoozeIcon />
								</IconButton>
							</div>
						)}
					</div> 
			</CardActions>
		);
	};

	public render() {
		const { showConfirmation, confirmationLabel } = this.state;
		const { classes, highlighted, areas  } = this.props;
		const availableMeals = this.props.meals;
		const {
			primaryName,
			date,
			time,
			area,
			meals,
			peopleCount,
			phoneNumber,
			email,
			notes,
			messages,
			snoozed,
			payment,
			rejected
		} = this.props.data;

		return (
			<Card
				className={classNames(classes.root, {
					[classes.rootSnoozed]: snoozed,
					[classes.rootRejected]: rejected,
					[classes.rootHighlighted]: highlighted,
					[classes.rootPaymentComplete]: payment && payment.state === "completed",
					[classes.rootPaymentExpired]: payment && payment.state === "expired",
					[classes.rootPaymentPending]: payment && payment.state === "awaiting_payment",
				})}
			>
				<CardHeader
					avatar={<Avatar>{peopleCount}</Avatar>}
					title={primaryName}
					subheader={formatDate(date)}
					action={ (!rejected) ? this.renderCardHeaderAction() : null }
					classes={{
						action: classes.cardHeaderAction
					}}
				/>
				<CardContent className={classes.content}>
					<div className={classes.contentGrid}>
						<div
							className={classNames(
								classes.dataGroup,
								classes.peopleCount
							)}
						>
							<div className={classes.dataIconContainer}>
								<PeopleIcon className={classes.dataIcon} />
							</div>
							<Typography className={classes.label}>{peopleCount} Personen</Typography>
						</div>
						{time && (
							<div
								className={classNames(
									classes.dataGroup,
									classes.time
								)}
							>
								<div className={classes.dataIconContainer}>
									<AccessTimeIcon className={classes.dataIcon} />
								</div>
								<Typography className={classes.label}>{format(time, "HH:mm")} Uhr</Typography>
							</div>
						)}
						{area && (
							<div
								className={classNames(
									classes.dataGroup,
									classes.area
								)}
							>
								<div className={classes.dataIconContainer}>
									<LayersIcon className={classes.dataIcon} />
								</div>
								<Typography className={classes.label}>{areas.find(a => a.id === area).name}</Typography>
							</div>
						)}
						{phoneNumber && (
							<div
								className={classNames(
									classes.dataGroup,
									classes.phoneNumber
								)}
							>
								<div className={classes.dataIconContainer}>
									<PhoneIcon className={classes.dataIcon} />
								</div>
								<Typography className={classes.label}>{phoneNumber}</Typography>
							</div>
						)}
						{meals && Object.keys(meals).length > 0 && (
							<div
								className={classNames(
									classes.dataGroup,
									classes.meals
								)}
							>
								<div className={classes.dataIconContainer}>
									<RestaurantIcon className={classes.dataIcon} />
								</div>
								<Typography>
									{Object.keys(meals).map(id => (
										<div key={id}>{meals[id]}x {availableMeals.find(m => m.id === id).name}</div>
									))}
								</Typography>
							</div>
						)}
						<div
							className={classNames(classes.dataGroup, classes.email)}
						>
							<div className={classes.dataIconContainer}>
								<AlternateEmailIcon className={classes.dataIcon} />
							</div>
							<Typography className={classes.label}>{email}</Typography>
						</div>
						{payment && (
							<div
								className={classNames(
									classes.dataGroup,
									classes.payment
								)}
							>
								<div className={classes.dataIconContainer}>
									<AttachMoneyIcon className={classes.dataIcon} />
								</div>
								<Typography dangerouslySetInnerHTML={{ __html: this.renderPaymentInfo() }} />
							</div>
						)}
						{notes && (
							<div
								className={classNames(
									classes.dataGroup,
									classes.notes
								)}
							>
								<div className={classes.dataIconContainer}>
									<InfoIcon className={classes.dataIcon} />
								</div>
								<Typography dangerouslySetInnerHTML={{ __html: nl2br(notes) }} />
							</div>
						)}
						{messages && messages.length > 0 && (
							<div
								className={classNames(
									classes.dataGroup,
									classes.messages
								)}
							>
								<div className={classes.dataIconContainer}>
									<ChatIcon className={classes.dataIcon} />
								</div>
								<Typography>
									{messages.length}{" "}
									{messages.length > 1
										? "Nachrichten"
										: "Nachricht"}
								</Typography>
							</div>
						)}
					</div>
				</CardContent>
				{ this.renderCardActions() }
			</Card>
		);
	}
}

const styles = (theme: Theme) =>
	createStyles({
		root: {
			margin: 10,
			width: "calc(33.33% - 20px)",
			display: "flex",
			flexDirection: "column",
			borderTop: "5px solid white",

			"@media (max-width: 1160px)": {
				width: "calc(50% - 20px)"
			},
			"@media (max-width: 767px)": {
				width: "calc(100% - 20px)"
			}
		},
		rootSnoozed: { opacity: 0.5 },
		rootRejected: { opacity: 0.5 },
		rootHighlighted: {
			boxShadow: `0 0 12px ${theme.palette.primary.main}75`
		},
		rootPaymentPending: {
			borderColor: theme.status.payment.awaitingPayment
		},
		rootPaymentComplete: {
			borderColor: theme.status.payment.completed
		},
		rootPaymentExpired: {
			borderColor: theme.status.payment.expired
		},
		flexExpand: {
			flexGrow: 1
		},
		content: {
			background: "#FAFAFA",
			flexGrow: 1,
		},
		contentGrid: {
			display: "grid",
			gridTemplateColumns: "50% 50%",
			gridGap: "7px"
		},
		cardActions: {
			position: "relative",
			width: "100%"
		},
		cardHeaderAction: {
			alignSelf: "center"
		},
		confirmationActions: {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			display: "flex",
			alignItems: "center",
			pointerEvents: "none",
			opacity: 0,
			transition: "opacity 0.2s ease",
			background: "white",
			zIndex: 5
		},
		confirmationActionsVisible: {
			pointerEvents: "all",
			opacity: 1
		},
		dataGroup: {
			display: "flex",

			// "* + &": {
			// 	marginTop: 5
			// }
		},
		dataIconContainer: {
			marginRight: 10
		},
		dataIcon: {
			color: "#797979"
		},
		confirmationLabel: {
			fontWeight: 500,
			flexGrow: 1
		},
		buttonContainer: {
			display: "flex"
		},
		label: {
			overflow: "hidden",
			textOverflow: "ellipsis",
		},
		peopleCount: {},
		time: {},
		area: {},
		meals: {},
		phoneNumber: {},
		email: {},
		notes: {
			gridColumn: "span 2"
		},
		messages: {},
		payment: {
			gridColumn: "span 2"
		},
		snoozeButton: {},
		acceptButton: {},
		rejectButton: {},
		editButton: {},
		deleteButton: {},
		replyButton: {},
		convertToReservervationButton: {},
		requestPaymentButton: {},
		confirmationOKButton: {},
		confirmationCancelButton: {}
	});

const mapStateToProps = (state: StoreState) => ({
	areas: state.areas,
	meals: state.settings.meals
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	openAddPaymentDialog: (inquiryId: string, email: string, name: string) => dispatch(openAddPaymentDialog(inquiryId, email, name)),
	openEditInquiryDialog: (inquiry: Inquiry) => dispatch(openEditInquiryDialog(inquiry))
});

export const ConnectedInquiry = connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Inquiry));
